import type { EntrySkeletonType, EntryCollection, Entry } from 'contentful'

export const ALLOWED_CONTENT_TYPES = [
  'blog_article',
  'connected_content_pillar',
  'content_page',
  'my_bag',
  'product_listing_page',
  'product_detail_page',
  'slug',
] as const

export type PreviewConfig = {
  previewLocale: CntflLocales
  previewEnv: string
  previewByDate: string
  previewId?: string
  previewPersonalizationTag?: string
  // Resolved market, not the raw query param — see useContentfulPreview.
  previewMarket?: string
  productId?: string
}

export type PromotionPreviewConfig = {
  siteDate?: string
  customerGroup?: string
}

export type PageEntry = Entry<EntrySkeletonType, undefined, string>
export type PanelInfo = {
  id: string
  label?: string
  contentType?: string
  status: {
    isDraft: boolean
    isChanged: boolean
    isPublished: boolean
    isUnpublished: boolean
    isArchived: boolean
  }
  dateInfo: {
    createdAt?: string
    firstPublishedAt?: string
    publishedAt?: string
    updatedAt?: string
    archivedAt?: string
    publishedVersion?: number
    revision?: number
  }
  isParentPage: boolean
}
export type LocalePageEntry = Entry<EntrySkeletonType, undefined, string> & {
  sys: {
    id: string
    revision?: number
    updatedAt?: string
    publishedAt?: string
    firstPublishedAt?: string
    archivedAt?: string
    createdAt?: string
    publishedVersion?: number
  }
}
export type PageFields = {
  panels?: LocalePageEntry[]
  personalizationTags?: string[]
  [key: string]: any
}

// ---- Scheduled Actions Types ----
export type EntryDisplayDetails = {
  show: 'preview' | 'published' | false
  label?: string
}
export type DisplayInfo = {
  [id: string]: EntryDisplayDetails
}
export type ScheduleAction = {
  action: 'publish' | 'unpublish'
  scheduledFor: {
    datetime: Date
  }
}
export type SchedulingInfo<T = ScheduleAction> = {
  status: {
    isDraft: boolean
    isChanged: boolean
    isPublished: boolean
    isArchived: boolean
  }
  schedule: T[]
}
export type ScheduleEntry = {
  action: string
  scheduledFor: {
    datetime: Date
  }
}
export type PanelStatus = {
  id: string
  label?: string
  status: {
    isDraft: boolean
    isChanged: boolean
    isPublished: boolean
    isArchived: boolean
  }
}
export type ScheduledActionItem = {
  action: 'publish' | 'unpublish'
  entity: Entry<EntrySkeletonType, undefined, string> & {
    sys: {
      id: string
      type: string
      linkType: string
      contentType?: {
        sys: {
          id: string
          type: string
          linkType: string
        }
      }
    }
  }
  scheduledFor: {
    datetime: string
  }
}

export type PreviewFilterFunction = () => {
  transformContent?: (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => any
  filterByLocale?: (entries: EntryCollection<EntrySkeletonType, undefined, string>, previewConfig: PreviewConfig) => any
  filterByPreviewId?: (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => any
  filterByPersonalizationTag?: (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => any
  filterByMarket?: (entries: EntryCollection<EntrySkeletonType, undefined, string>, previewConfig: PreviewConfig) => any
  filterByPreviewDate?: (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => Promise<any>
  getAllPanels?: (
    data: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => PanelInfo[]
}

export type PreviewFilters = Record<string, PreviewFilterFunction>
